import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios, useApiResponseParser, useAuth } from "../";

export default function useContractTemplate() {
    const { axiosPrivate } = useAxios();
    const { parseSuccessResponseData } = useApiResponseParser();
    const queryClient = useQueryClient();
    const { selectedOrganization } = useAuth();
    const organizationId = selectedOrganization?.Id || "";

    function useList() {
        return useQuery({
            queryKey: ["contract-templates", organizationId],
            queryFn: async () => {
                if (!organizationId) return [];
                const response = await axiosPrivate.get(`/v1/document/template/list?OrganizationId=${organizationId}`);
                return parseSuccessResponseData(response) as any[];
            },
            enabled: !!organizationId,
        });
    }

    function useParseFields() {
        return useMutation({
            mutationFn: async (file: File) => {
                const formData = new FormData();
                formData.append("file", file);
                formData.append("OrganizationId", organizationId);
                const response = await axiosPrivate.post(`/v1/document/template/parse-fields?OrganizationId=${organizationId}`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                return parseSuccessResponseData(response) as { fields: string[] };
            },
        });
    }

    function useCreate() {
        return useMutation({
            mutationFn: async (data: { name: string; type: string; file: File; mappingConfig: any }) => {
                const formData = new FormData();
                formData.append("name", data.name);
                formData.append("type", data.type);
                formData.append("file", data.file);
                formData.append("mappingConfig", JSON.stringify(data.mappingConfig));
                formData.append("OrganizationId", organizationId);

                const response = await axiosPrivate.post(`/v1/document/template?OrganizationId=${organizationId}`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                return parseSuccessResponseData(response);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ["contract-templates"] });
            },
        });
    }

    function useDelete() {
        return useMutation({
            mutationFn: (id: string) =>
                axiosPrivate.delete(`/v1/document/template/${id}?OrganizationId=${organizationId}`).then(parseSuccessResponseData),
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ["contract-templates"] });
            },
        });
    }

    return {
        useList,
        useParseFields,
        useCreate,
        useDelete,
    };
}
